import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { useLocation } from '../context/LocationContext';
import { supabase } from '../services/supabase';
import { thumbUrl } from '../utils/imageUrl';
import { compressImage } from '../utils/compressImage';
import LocationBanner from '../components/LocationBanner';

const MenuPage = () => {
  const { business } = useAuth();
  const { locationId, activeLocation, locations, isOwner } = useLocation();
  const isChain = isOwner && locations.length > 1;
  const [winW, setWinW] = useState(window.innerWidth);
  useEffect(() => {
    const fn = () => setWinW(window.innerWidth);
    window.addEventListener('resize', fn);
    return () => window.removeEventListener('resize', fn);
  }, []);
  const isMobile = winW < 640;
  const [categories, setCategories] = useState([]);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState('');
  const [imageUploading, setImageUploading] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);

  const menuImageInputRef = useRef(null);
  const menuImgInputRef = useRef(null);
  const loadMenuCallId = useRef(0);

  // ── Image Menu state ──────────────────────────────────────────────────────
  const [menuDisplayType, setMenuDisplayType] = useState('structured');
  // sections: [{id, title, images:[{url}]}]
  const [menuSections, setMenuSections] = useState([]);
  const [menuImgUploading, setMenuImgUploading] = useState(false);
  const uploadSectionIdx = useRef(null);
  const imgDragSec = useRef(null);
  const imgDragIdx = useRef(null);
  const imgDragOverIdx = useRef(null);
  const secDragIdx = useRef(null);
  const secDragOverIdx = useRef(null);

  // Category form
  const [newCatName, setNewCatName] = useState('');
  const [newCatStation, setNewCatStation] = useState('Mətbəx');
  const [editingCat, setEditingCat] = useState(null);

  const STATIONS = ['Mətbəx', 'Kababxana', 'Qutab', 'Bar', 'Sərqiyyat', 'Digər'];

  // Item form modal
  const [itemModal, setItemModal] = useState(null); // null | { categoryId, item? }
  // Scope: 'global' = bütün filiallara, 'local' = xüsusi filial
  const [itemScopeType, setItemScopeType] = useState('global');
  const [itemTargetLocId, setItemTargetLocId] = useState(null);
  const [itemForm, setItemForm] = useState({
    name: '', description: '',
    name_en: '', description_en: '',
    price: '', is_available: true, target_audience: [], image_url: '',
    variants: [], // [{label, price}]
  });

  const PRESET_SIZES = ['Kiçik', 'Orta', 'Böyük'];

  const addVariant = () =>
    setItemForm(prev => ({ ...prev, variants: [...prev.variants, { label: '', price: '' }] }));

  const updateVariant = (i, field, val) =>
    setItemForm(prev => {
      const v = [...prev.variants];
      v[i] = { ...v[i], [field]: val };
      return { ...prev, variants: v };
    });

  const removeVariant = (i) =>
    setItemForm(prev => ({ ...prev, variants: prev.variants.filter((_, idx) => idx !== i) }));

  const applyPreset = () =>
    setItemForm(prev => ({ ...prev, variants: PRESET_SIZES.map(label => ({ label, price: '' })) }));

  const showMsg = (msg) => {
    setMessage(msg);
    setTimeout(() => setMessage(''), 3000);
  };

  // ── Load Data ──────────────────────────────────────────────────────────────
  const loadMenu = useCallback(async () => {
    if (!business?.id) return;
    const callId = ++loadMenuCallId.current;
    setLoading(true);
    try {
      let catQuery  = supabase.from('menu_categories').select('*').eq('business_id', business.id).order('sort_order');
      let itemQuery = supabase.from('menu_items').select('*').eq('business_id', business.id).order('sort_order');
      if (locationId) {
        catQuery  = catQuery.eq('location_id', locationId);
        itemQuery = itemQuery.eq('location_id', locationId);
      }
      const [catRes, itemRes, ratingRes] = await Promise.all([
        catQuery,
        itemQuery,
        supabase.from('menu_item_reviews').select('menu_item_id, rating').eq('business_id', business.id),
      ]);
      if (callId !== loadMenuCallId.current) return;
      if (catRes.error) throw catRes.error;
      if (itemRes.error) throw itemRes.error;
      const cats = locationId
        ? (catRes.data  || []).filter(c => c.location_id === locationId)
        : (catRes.data  || []);
      const rawItems = locationId
        ? (itemRes.data || []).filter(i => i.location_id === locationId)
        : (itemRes.data || []);
      setCategories(cats);
      const ratingMap = {};
      (ratingRes.data || []).forEach(r => {
        if (!ratingMap[r.menu_item_id]) ratingMap[r.menu_item_id] = { sum: 0, count: 0 };
        ratingMap[r.menu_item_id].sum   += r.rating;
        ratingMap[r.menu_item_id].count += 1;
      });
      const itemsWithRating = rawItems.map(item => {
        const rm = ratingMap[item.id];
        return rm ? { ...item, avg_rating: (rm.sum / rm.count).toFixed(1), review_count: rm.count } : item;
      });
      setItems(itemsWithRating);
    } catch (err) {
      if (callId !== loadMenuCallId.current) return;
      showMsg('❌ Məlumat yüklənmədi: ' + err.message);
    } finally {
      if (callId !== loadMenuCallId.current) return;
      setLoading(false);
    }
  }, [business?.id, locationId]);

  useEffect(() => { loadMenu(); }, [loadMenu]);

  useEffect(() => {
    if (!business?.id) return;
    const src = locationId ? activeLocation : business;
    if (!src) return;
    setMenuDisplayType(src.menu_display_type || 'structured');
    const raw = src.menu_images || [];
    // migrate old flat [{url}] format to sections
    if (raw.length > 0 && !raw[0]?.images) {
      setMenuSections([{ id: '1', title: '', images: raw }]);
    } else {
      setMenuSections(raw);
    }
  }, [business?.id, locationId, activeLocation]);

  // ── Image Menu CRUD ────────────────────────────────────────────────────────
  const persistSections = async (sections) => {
    setMenuSections(sections);
    try {
      if (locationId) {
        await supabase.from('business_locations').update({ menu_images: sections }).eq('id', locationId);
      } else {
        await supabase.from('businesses').update({ menu_images: sections }).eq('id', business.id);
      }
    } catch (err) {
      showMsg('❌ ' + err.message);
    }
  };

  const saveDisplayType = async (type) => {
    setMenuDisplayType(type);
    try {
      if (locationId) {
        await supabase.from('business_locations').update({ menu_display_type: type }).eq('id', locationId);
      } else {
        await supabase.from('businesses').update({ menu_display_type: type }).eq('id', business.id);
      }
      showMsg(type === 'images' ? '✅ Ziyarətçilər şəkil menyunu görəcək' : '✅ Ziyarətçilər strukturlu menyunu görəcək');
    } catch (err) {
      showMsg('❌ ' + err.message);
    }
  };

  const addSection = () => {
    const newSec = { id: Date.now().toString(), title: '', images: [] };
    persistSections([...menuSections, newSec]);
  };

  const updateSectionTitle = (sIdx, title) => {
    setMenuSections(prev => prev.map((s, i) => i === sIdx ? { ...s, title } : s));
  };

  const saveSectionTitle = async (sIdx) => {
    await persistSections(menuSections);
  };

  const deleteSection = async (sIdx) => {
    if (!window.confirm('Bu bölməni və içindəki şəkilləri silmək istəyirsiniz?')) return;
    await persistSections(menuSections.filter((_, i) => i !== sIdx));
    showMsg('✅ Bölmə silindi');
  };

  const handleMenuImgUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    const sIdx = uploadSectionIdx.current;
    if (!files.length || sIdx === null) return;
    setMenuImgUploading(true);
    try {
      const updated = menuSections.map((s, i) => i === sIdx ? { ...s, images: [...s.images] } : s);
      for (const file of files) {
        const compressed = await compressImage(file, { maxWidth: 1400, quality: 0.88 });
        const ext = compressed.type?.includes('png') ? 'png' : 'jpg';
        const path = `menu-images/${business.id}/${Date.now()}_${Math.random().toString(36).slice(2)}.${ext}`;
        const { error: upErr } = await supabase.storage
          .from('business-photos').upload(path, compressed, { contentType: compressed.type || 'image/jpeg' });
        if (upErr) throw upErr;
        const { data: urlData } = supabase.storage.from('business-photos').getPublicUrl(path);
        updated[sIdx].images.push({ url: urlData.publicUrl });
      }
      await persistSections(updated);
      if (menuImgInputRef.current) menuImgInputRef.current.value = '';
      showMsg(`✅ ${files.length} şəkil yükləndi`);
    } catch (err) {
      showMsg('❌ ' + err.message);
    } finally {
      setMenuImgUploading(false);
    }
  };

  const removeMenuImage = async (sIdx, imgIdx) => {
    if (!window.confirm('Bu şəkili silmək istəyirsiniz?')) return;
    const updated = menuSections.map((s, i) =>
      i === sIdx ? { ...s, images: s.images.filter((_, j) => j !== imgIdx) } : s
    );
    await persistSections(updated);
    showMsg('✅ Şəkil silindi');
  };

  // Image drag within a section
  const onImgDragStart = (sIdx, imgIdx) => { imgDragSec.current = sIdx; imgDragIdx.current = imgIdx; };
  const onImgDragOver = (e, imgIdx) => { e.preventDefault(); imgDragOverIdx.current = imgIdx; };
  const onImgDrop = async (sIdx) => {
    const from = imgDragIdx.current;
    const to = imgDragOverIdx.current;
    if (from === null || to === null || from === to || imgDragSec.current !== sIdx) return;
    const updated = menuSections.map((s, i) => {
      if (i !== sIdx) return s;
      const imgs = [...s.images];
      const [moved] = imgs.splice(from, 1);
      imgs.splice(to, 0, moved);
      return { ...s, images: imgs };
    });
    imgDragSec.current = null; imgDragIdx.current = null; imgDragOverIdx.current = null;
    await persistSections(updated);
  };

  // Section drag (reorder sections)
  const onSecDragStart = (sIdx) => { secDragIdx.current = sIdx; };
  const onSecDragOver = (e, sIdx) => { e.preventDefault(); secDragOverIdx.current = sIdx; };
  const onSecDrop = async () => {
    const from = secDragIdx.current;
    const to = secDragOverIdx.current;
    if (from === null || to === null || from === to) return;
    const reordered = [...menuSections];
    const [moved] = reordered.splice(from, 1);
    reordered.splice(to, 0, moved);
    secDragIdx.current = null; secDragOverIdx.current = null;
    await persistSections(reordered);
  };

  // ── Category CRUD ──────────────────────────────────────────────────────────
  const addCategory = async () => {
    if (!newCatName.trim()) return;
    try {
      let { data, error } = await supabase.from('menu_categories').insert({
        business_id: business.id,
        location_id: locationId || null,
        name: newCatName.trim(),
        station: newCatStation,
        sort_order: categories.length,
      }).select().single();
      if (error?.code === '42703') {
        ({ data, error } = await supabase.from('menu_categories').insert({
          business_id: business.id,
          name: newCatName.trim(),
          station: newCatStation,
          sort_order: categories.length,
        }).select().single());
      }
      if (error) throw error;
      setNewCatName('');
      setNewCatStation('Mətbəx');
      setCategories(prev => [...prev, data]);
      showMsg('✅ Kateqoriya əlavə edildi');
    } catch (err) {
      showMsg('❌ ' + err.message);
    }
  };

  const saveCategory = async (cat) => {
    try {
      const { error } = await supabase
        .from('menu_categories').update({ name: editingCat.name, station: editingCat.station }).eq('id', cat.id);
      if (error) throw error;
      setCategories(prev => prev.map(c => c.id === cat.id ? { ...c, name: editingCat.name, station: editingCat.station } : c));
      setEditingCat(null);
      showMsg('✅ Kateqoriya yeniləndi');
    } catch (err) {
      showMsg('❌ ' + err.message);
    }
  };

  const deleteCategory = async (catId) => {
    if (!window.confirm('Bu kateqoriyanı və bütün məhsullarını silmək istəyirsiniz?')) return;
    try {
      const { error } = await supabase.from('menu_categories').delete().eq('id', catId);
      if (error) throw error;
      setCategories(prev => prev.filter(c => c.id !== catId));
      setItems(prev => prev.filter(i => i.category_id !== catId));
      showMsg('✅ Kateqoriya silindi');
    } catch (err) {
      showMsg('❌ ' + err.message);
    }
  };

  const reorderCategory = async (cat, direction) => {
    const idx = categories.findIndex(c => c.id === cat.id);
    const swapIdx = direction === 'up' ? idx - 1 : idx + 1;
    if (swapIdx < 0 || swapIdx >= categories.length) return;

    const swapCat = categories[swapIdx];
    const newOrder = [...categories];
    newOrder[idx] = { ...swapCat, sort_order: cat.sort_order };
    newOrder[swapIdx] = { ...cat, sort_order: swapCat.sort_order };
    newOrder.sort((a, b) => a.sort_order - b.sort_order);
    setCategories(newOrder);

    try {
      await Promise.all([
        supabase.from('menu_categories').update({ sort_order: swapCat.sort_order }).eq('id', cat.id),
        supabase.from('menu_categories').update({ sort_order: cat.sort_order }).eq('id', swapCat.id),
      ]);
    } catch (err) {
      showMsg('❌ ' + err.message);
      loadMenu();
    }
  };

  // ── Item CRUD ──────────────────────────────────────────────────────────────
  const openAddItem = (categoryId) => {
    setItemForm({ name: '', description: '', name_en: '', description_en: '', price: '', is_available: true, target_audience: [], image_url: '', variants: [] });
    setItemScopeType(isChain ? 'global' : 'local');
    setItemTargetLocId(locations[0]?.id || null);
    setItemModal({ categoryId, item: null });
  };

  const openEditItem = (item) => {
    setItemForm({
      name: item.name || '',
      description: item.description || '',
      name_en: item.name_en || '',
      description_en: item.description_en || '',
      price: item.price?.toString() || '',
      is_available: item.is_available,
      target_audience: item.target_audience || [],
      image_url: item.image_url || '',
      variants: item.variants || [],
    });
    setItemScopeType(item.location_id ? 'local' : 'global');
    setItemTargetLocId(item.location_id || locations[0]?.id || null);
    setItemModal({ categoryId: item.category_id, item });
  };

  // ── Upload menu item image ─────────────────────────────────────────────────
  const handleMenuImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImageUploading(true);
    try {
      const compressed = await compressImage(file, { maxWidth: 800, quality: 0.82 });
      const path = `menu/${business.id}/${Date.now()}.jpg`;
      const { error: upErr } = await supabase.storage
        .from('business-photos')
        .upload(path, compressed, { contentType: 'image/jpeg' });
      if (upErr) throw upErr;
      const { data: urlData } = supabase.storage.from('business-photos').getPublicUrl(path);
      setItemForm(prev => ({ ...prev, image_url: urlData.publicUrl }));
    } catch (err) {
      showMsg('❌ Şəkil xətası: ' + err.message);
    } finally {
      setImageUploading(false);
    }
  };

  const saveItem = async () => {
    const hasVariants = itemForm.variants.length > 0;
    if (!itemForm.name.trim()) { showMsg('❌ Ad mütləqdir'); return; }
    if (!hasVariants && !itemForm.price) { showMsg('❌ Qiymət və ya ölçü variantı mütləqdir'); return; }
    if (hasVariants && itemForm.variants.some(v => !v.label.trim() || !v.price)) { showMsg('❌ Bütün variantların adı və qiyməti doldurulmalıdır'); return; }

    const cleanVariants = itemForm.variants.map(v => ({ label: v.label.trim(), price: parseFloat(v.price) }));
    const basePrice = hasVariants ? parseFloat(itemForm.variants[0]?.price || 0) : parseFloat(itemForm.price);

    const payload = {
      name: itemForm.name.trim() || itemForm.name_en.trim(),
      description: itemForm.description.trim(),
      name_en: itemForm.name_en.trim() || null,
      description_en: itemForm.description_en.trim() || null,
      price: basePrice,
      variants: cleanVariants,
      is_available: itemForm.is_available,
      target_audience: itemForm.target_audience,
      image_url: itemForm.image_url || null,
    };

    try {
      // location_id-ni scope-a görə müəyyən et
      const resolvedLocationId = isChain
        ? (itemScopeType === 'global' ? null : (itemTargetLocId || null))
        : (locationId || null);

      if (itemModal.item) {
        const { error } = await supabase.from('menu_items')
          .update({ ...payload, location_id: resolvedLocationId })
          .eq('id', itemModal.item.id);
        if (error) throw error;
        setItems(prev => prev.map(i => i.id === itemModal.item.id
          ? { ...i, ...payload, location_id: resolvedLocationId } : i));
      } else {
        const sortOrder = items.filter(i => i.category_id === itemModal.categoryId).length;
        let { data, error } = await supabase.from('menu_items').insert({
          business_id: business.id,
          location_id: resolvedLocationId,
          category_id: itemModal.categoryId,
          ...payload,
          sort_order: sortOrder,
        }).select().single();
        if (error?.code === '42703') {
          ({ data, error } = await supabase.from('menu_items').insert({
            business_id: business.id,
            category_id: itemModal.categoryId,
            ...payload,
            sort_order: sortOrder,
          }).select().single());
        }
        if (error) throw error;
        setItems(prev => [...prev, data]);
      }
      setItemModal(null);
      showMsg(itemModal.item ? '✅ Məhsul yeniləndi' : '✅ Məhsul əlavə edildi');
    } catch (err) {
      showMsg('❌ ' + err.message);
    }
  };

  const toggleAvailability = async (item) => {
    setItems(prev => prev.map(i => i.id === item.id ? { ...i, is_available: !item.is_available } : i));
    try {
      const { error } = await supabase.from('menu_items')
        .update({ is_available: !item.is_available }).eq('id', item.id);
      if (error) throw error;
    } catch (err) {
      setItems(prev => prev.map(i => i.id === item.id ? { ...i, is_available: item.is_available } : i));
      showMsg('❌ ' + err.message);
    }
  };

  const deleteItem = async (itemId) => {
    if (!window.confirm('Bu məhsulu silmək istəyirsiniz?')) return;
    try {
      const { error } = await supabase.from('menu_items').delete().eq('id', itemId);
      if (error) throw error;
      setItems(prev => prev.filter(i => i.id !== itemId));
      showMsg('✅ Məhsul silindi');
    } catch (err) {
      showMsg('❌ ' + err.message);
    }
  };

  if (loading) return (
    <div style={{ display: 'flex', justifyContent: 'center', padding: 60 }}>
      <div style={{ fontSize: 18, color: '#667eea' }}>Menyu yüklənir...</div>
    </div>
  );

  const menuId = locationId || business?.id;
  const menuUrl = menuId ? `https://cafegoo.com/menu/${menuId}` : '';
  const qrImageUrl = menuUrl
    ? `https://api.qrserver.com/v1/create-qr-code/?size=300x300&data=${encodeURIComponent(menuUrl)}&bgcolor=ffffff&color=1a1a2e&margin=16`
    : '';

  const copyLink = () => {
    navigator.clipboard.writeText(menuUrl);
    setLinkCopied(true);
    setTimeout(() => setLinkCopied(false), 2000);
  };

  const downloadQR = () => {
    const dlUrl = `https://api.qrserver.com/v1/create-qr-code/?size=600x600&data=${encodeURIComponent(menuUrl)}&bgcolor=ffffff&color=1a1a2e&margin=20`;
    const a = document.createElement('a');
    a.href = dlUrl;
    a.download = `${activeLocation?.name || business?.name || 'menu'}-qr.png`;
    a.target = '_blank';
    a.click();
  };

  return (
    <div style={{ padding: isMobile ? '16px 12px' : 24, maxWidth: 900, margin: '0 auto' }}>
      <div style={{ marginBottom: isMobile ? 16 : 24 }}>
        <h1 style={{ fontSize: isMobile ? 20 : 26, fontWeight: 800, color: '#1A1A2E' }}>🍽️ Menyu İdarəetməsi</h1>
        <p style={{ color: '#888', fontSize: isMobile ? 13 : 14 }}>Kateqoriyalar və məhsullar əlavə edin, redaktə edin</p>
      </div>

      <LocationBanner />

      {/* ── QR Menyu Kartı ── */}
      {business?.id && (
        <div style={{
          background: 'linear-gradient(145deg, #0d0d1a 0%, #1a1a2e 55%, #0f1829 100%)',
          borderRadius: 24,
          marginBottom: 28,
          overflow: 'hidden',
          boxShadow: '0 12px 48px rgba(0,0,0,0.35)',
          position: 'relative',
        }}>
          {/* Glow decoration */}
          <div style={{
            position: 'absolute', top: -80, right: -80,
            width: 260, height: 260, borderRadius: '50%',
            background: 'radial-gradient(circle, rgba(245,158,11,0.12) 0%, transparent 65%)',
            pointerEvents: 'none',
          }} />
          <div style={{
            position: 'absolute', bottom: -60, left: -60,
            width: 180, height: 180, borderRadius: '50%',
            background: 'radial-gradient(circle, rgba(102,126,234,0.1) 0%, transparent 65%)',
            pointerEvents: 'none',
          }} />

          {/* Header */}
          <div style={{
            padding: isMobile ? '16px 18px' : '18px 28px',
            borderBottom: '1px solid rgba(255,255,255,0.07)',
            display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12,
            position: 'relative',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <div style={{
                width: 44, height: 44, borderRadius: 14,
                background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 22, flexShrink: 0,
                boxShadow: '0 4px 16px rgba(245,158,11,0.4)',
              }}>📱</div>
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                  <span style={{ color: '#fff', fontWeight: 800, fontSize: isMobile ? 14 : 16, letterSpacing: -0.3 }}>
                    Rəqəmsal Menyu
                  </span>
                  <span style={{
                    background: 'linear-gradient(135deg, #f59e0b, #d97706)',
                    color: '#fff', fontSize: 9, fontWeight: 800,
                    padding: '2px 7px', borderRadius: 5, letterSpacing: 0.8,
                    boxShadow: '0 2px 8px rgba(245,158,11,0.4)',
                  }}>PRO</span>
                </div>
                <div style={{ color: 'rgba(255,255,255,0.42)', fontSize: 12, marginTop: 3 }}>
                  {activeLocation?.name
                    ? `${activeLocation.name} — masaya qoyun, müştəri skan etsin`
                    : (business?.name ? `${business.name} — QR kodu masalara yapışdırın` : 'QR kodu çap edib masalara yapışdırın')}
                </div>
              </div>
            </div>
            <div style={{
              display: 'flex', alignItems: 'center', gap: 6,
              padding: '6px 14px', borderRadius: 20,
              background: 'rgba(74,222,128,0.1)',
              border: '1px solid rgba(74,222,128,0.25)',
              flexShrink: 0,
            }}>
              <div style={{ width: 7, height: 7, borderRadius: '50%', background: '#4ade80', boxShadow: '0 0 6px #4ade80' }} />
              <span style={{ color: '#4ade80', fontSize: 12, fontWeight: 700 }}>Aktiv</span>
            </div>
          </div>

          {/* Body */}
          <div style={{
            padding: isMobile ? '20px 18px' : '28px',
            display: 'flex', gap: isMobile ? 20 : 32,
            alignItems: isMobile ? 'center' : 'flex-start',
            flexDirection: isMobile ? 'column' : 'row',
            position: 'relative',
          }}>
            {/* QR Code Block */}
            <div style={{ flexShrink: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12 }}>
              <div style={{
                background: '#fff',
                borderRadius: 20,
                padding: isMobile ? 10 : 14,
                boxShadow: '0 8px 32px rgba(0,0,0,0.5)',
                position: 'relative',
              }}>
                {/* Corner accents */}
                {[
                  { top: 0, left: 0, borderTop: '3px solid #f59e0b', borderLeft: '3px solid #f59e0b', borderRadius: '4px 0 0 0' },
                  { top: 0, right: 0, borderTop: '3px solid #f59e0b', borderRight: '3px solid #f59e0b', borderRadius: '0 4px 0 0' },
                  { bottom: 0, left: 0, borderBottom: '3px solid #f59e0b', borderLeft: '3px solid #f59e0b', borderRadius: '0 0 0 4px' },
                  { bottom: 0, right: 0, borderBottom: '3px solid #f59e0b', borderRight: '3px solid #f59e0b', borderRadius: '0 0 4px 0' },
                ].map((s, i) => (
                  <div key={i} style={{ position: 'absolute', width: 14, height: 14, ...s }} />
                ))}
                <img
                  src={qrImageUrl}
                  alt="QR Kod"
                  style={{
                    width: isMobile ? 110 : 140, height: isMobile ? 110 : 140,
                    display: 'block', borderRadius: 6,
                  }}
                />
              </div>
              <button
                onClick={downloadQR}
                style={{
                  padding: '9px 20px', borderRadius: 12, border: 'none',
                  background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                  color: '#fff', fontWeight: 700, fontSize: 12, cursor: 'pointer',
                  boxShadow: '0 4px 16px rgba(245,158,11,0.45)',
                  letterSpacing: 0.3, width: '100%',
                  transition: 'transform 0.15s, box-shadow 0.15s',
                }}
                onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-1px)'; e.currentTarget.style.boxShadow = '0 6px 20px rgba(245,158,11,0.55)'; }}
                onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 4px 16px rgba(245,158,11,0.45)'; }}
              >
                ⬇ QR Yüklə
              </button>
            </div>

            {/* Link + Info */}
            <div style={{ flex: 1, minWidth: 0, width: isMobile ? '100%' : 'auto' }}>
              {/* Menyu linki */}
              <div style={{ marginBottom: 14 }}>
                <div style={{ fontSize: 10, fontWeight: 700, color: 'rgba(255,255,255,0.35)', marginBottom: 8, textTransform: 'uppercase', letterSpacing: 1 }}>
                  Menyu Linki
                </div>
                <div style={{
                  display: 'flex', alignItems: 'center', gap: 8,
                  background: 'rgba(255,255,255,0.05)',
                  borderRadius: 12, padding: '10px 14px',
                  border: '1px solid rgba(255,255,255,0.1)',
                }}>
                  <span style={{
                    flex: 1, fontSize: 12, color: 'rgba(255,255,255,0.65)',
                    fontFamily: 'monospace', wordBreak: 'break-all', lineHeight: 1.4,
                  }}>
                    {menuUrl}
                  </span>
                  <button
                    onClick={copyLink}
                    style={{
                      flexShrink: 0, padding: '7px 14px', borderRadius: 8, border: 'none',
                      background: linkCopied ? 'rgba(74,222,128,0.2)' : 'rgba(255,255,255,0.1)',
                      color: linkCopied ? '#4ade80' : 'rgba(255,255,255,0.8)',
                      fontWeight: 700, fontSize: 12, cursor: 'pointer',
                      border: linkCopied ? '1px solid rgba(74,222,128,0.4)' : '1px solid rgba(255,255,255,0.15)',
                      transition: 'all 0.2s', whiteSpace: 'nowrap',
                    }}
                  >
                    {linkCopied ? '✓ Kopyalandı' : 'Kopyala'}
                  </button>
                </div>
              </div>

              {/* Bax button */}
              <a
                href={menuUrl}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 7,
                  padding: '10px 20px', borderRadius: 12,
                  background: 'rgba(255,255,255,0.08)',
                  border: '1px solid rgba(255,255,255,0.14)',
                  color: '#fff', textDecoration: 'none',
                  fontWeight: 700, fontSize: 13,
                  transition: 'background 0.2s',
                  marginBottom: 16,
                }}
                onMouseEnter={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.13)'; }}
                onMouseLeave={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.08)'; }}
              >
                🔗 Menyuya Bax
              </a>

              {/* Stat chips */}
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                {[
                  { icon: '📂', label: `${categories.length} Kateqoriya` },
                  { icon: '🍽️', label: `${items.length} Məhsul` },
                ].map((chip, i) => (
                  <div key={i} style={{
                    display: 'flex', alignItems: 'center', gap: 6,
                    padding: '6px 12px', borderRadius: 20,
                    background: 'rgba(255,255,255,0.06)',
                    border: '1px solid rgba(255,255,255,0.1)',
                    fontSize: 12, color: 'rgba(255,255,255,0.6)', fontWeight: 600,
                  }}>
                    <span>{chip.icon}</span>
                    <span>{chip.label}</span>
                  </div>
                ))}
              </div>

              {/* Tip */}
              <div style={{
                marginTop: 14, padding: '10px 14px', borderRadius: 12,
                background: 'rgba(245,158,11,0.08)',
                border: '1px solid rgba(245,158,11,0.2)',
                fontSize: 12, color: 'rgba(255,255,255,0.5)', lineHeight: 1.65,
                display: 'flex', alignItems: 'flex-start', gap: 8,
              }}>
                <span style={{ flexShrink: 0, marginTop: 1 }}>💡</span>
                <span>QR kodu çap edib masalara yapışdırın. Müştərilər skan edib menyunuzu görəcək.</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {message && (
        <div style={{
          padding: '12px 16px', borderRadius: 10, marginBottom: 20,
          background: message.includes('✅') ? '#e8f5e9' : '#ffebee',
          color: message.includes('✅') ? '#2e7d32' : '#c62828',
          fontWeight: 600,
        }}>{message}</div>
      )}

      {/* ── Menyu Tipi Toggle ── */}
      <div style={{ background: '#fff', borderRadius: 16, padding: isMobile ? 14 : 20, marginBottom: isMobile ? 16 : 24,
        boxShadow: '0 2px 12px rgba(0,0,0,0.08)' }}>
        <h3 style={{ marginBottom: 4, fontSize: isMobile ? 14 : 16, fontWeight: 700 }}>Menyu Tipi</h3>
        <p style={{ fontSize: 12, color: '#888', marginBottom: 14, marginTop: 0 }}>
          Ziyarətçilərə hansı menyu göstərilsin?
        </p>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          {[
            { id: 'structured', icon: '📋', label: 'Strukturlu Menyu', desc: 'Kateqoriya + məhsullar' },
            { id: 'images',     icon: '📷', label: 'Şəkil Menusu',     desc: 'Yüklənmiş şəkillər' },
          ].map(opt => (
            <div key={opt.id} onClick={() => saveDisplayType(opt.id)}
              style={{
                flex: 1, minWidth: 140, padding: '12px 16px', borderRadius: 12, cursor: 'pointer',
                border: menuDisplayType === opt.id ? '2px solid #667eea' : '2px solid #e0e0e0',
                background: menuDisplayType === opt.id ? '#f0f0ff' : '#fafafa',
                transition: 'all 0.15s',
              }}>
              <div style={{ fontSize: 20, marginBottom: 4 }}>{opt.icon}</div>
              <div style={{ fontWeight: 700, fontSize: 13, color: menuDisplayType === opt.id ? '#667eea' : '#333' }}>{opt.label}</div>
              <div style={{ fontSize: 11, color: '#888', marginTop: 2 }}>{opt.desc}</div>
              {menuDisplayType === opt.id && (
                <div style={{ marginTop: 6, fontSize: 11, fontWeight: 700, color: '#667eea' }}>✓ Aktiv</div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* ── Şəkil Menusu ── */}
      <div style={{ background: '#fff', borderRadius: 16, padding: isMobile ? 14 : 20, marginBottom: isMobile ? 16 : 24,
        boxShadow: '0 2px 12px rgba(0,0,0,0.08)' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16, flexWrap: 'wrap', gap: 8 }}>
          <h3 style={{ margin: 0, fontSize: isMobile ? 14 : 16, fontWeight: 700 }}>📷 Şəkil Menusu</h3>
          <button onClick={addSection}
            style={{ padding: '8px 16px', background: '#667eea', color: '#fff', border: 'none',
              borderRadius: 8, cursor: 'pointer', fontWeight: 700, fontSize: 13 }}>
            + Bölmə Əlavə Et
          </button>
        </div>

        {/* Hidden upload input */}
        <input ref={menuImgInputRef} type="file" accept="image/*" multiple style={{ display: 'none' }}
          onChange={handleMenuImgUpload} />

        {menuSections.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '30px 0', color: '#bbb' }}>
            <div style={{ fontSize: 36, marginBottom: 8 }}>🖼️</div>
            <div style={{ fontSize: 13 }}>Hələ bölmə yoxdur — "Bölmə Əlavə Et" düyməsinə basın</div>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            {menuSections.map((sec, sIdx) => (
              <div
                key={sec.id}
                draggable
                onDragStart={() => onSecDragStart(sIdx)}
                onDragOver={e => onSecDragOver(e, sIdx)}
                onDrop={onSecDrop}
                style={{ border: '1.5px solid #e8e8f0', borderRadius: 14, overflow: 'hidden',
                  background: '#fafafa', cursor: 'default' }}
              >
                {/* Section header */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 12px',
                  background: '#f2f2fa', borderBottom: '1px solid #e8e8f0' }}>
                  <span style={{ fontSize: 15, color: '#bbb', cursor: 'grab', paddingRight: 2 }}
                    title="Bölməni sürüşdür">⠿</span>
                  <input
                    value={sec.title}
                    onChange={e => updateSectionTitle(sIdx, e.target.value)}
                    onBlur={() => saveSectionTitle(sIdx)}
                    placeholder="Bölmə başlığı (məs. Salatlar, İçkilər...)"
                    style={{ flex: 1, padding: '6px 10px', borderRadius: 7, border: '1.5px solid #ddd',
                      fontSize: 13, fontWeight: 600, outline: 'none', background: '#fff' }}
                  />
                  <button
                    onClick={() => { uploadSectionIdx.current = sIdx; menuImgInputRef.current?.click(); }}
                    disabled={menuImgUploading}
                    style={{ padding: '6px 12px', background: '#667eea', color: '#fff', border: 'none',
                      borderRadius: 7, cursor: 'pointer', fontWeight: 700, fontSize: 12,
                      whiteSpace: 'nowrap', opacity: menuImgUploading ? 0.6 : 1, flexShrink: 0 }}>
                    {menuImgUploading && uploadSectionIdx.current === sIdx ? 'Yüklənir...' : '+ Şəkil'}
                  </button>
                  <button onClick={() => deleteSection(sIdx)}
                    style={{ background: '#fee2e2', color: '#e74c3c', border: 'none',
                      borderRadius: 7, width: 30, height: 30, cursor: 'pointer', fontSize: 14,
                      fontWeight: 700, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    🗑
                  </button>
                </div>

                {/* Images horizontal slider */}
                {sec.images.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: '20px 0', color: '#ccc', fontSize: 13 }}>
                    Şəkil yoxdur — "+ Şəkil" düyməsinə basın
                  </div>
                ) : (
                  <div style={{ display: 'flex', gap: 10, overflowX: 'auto', padding: '12px',
                    scrollSnapType: 'x mandatory', WebkitOverflowScrolling: 'touch',
                    scrollbarWidth: 'thin', scrollbarColor: '#ddd transparent' }}>
                    {sec.images.map((img, imgIdx) => (
                      <div
                        key={imgIdx}
                        draggable
                        onDragStart={() => onImgDragStart(sIdx, imgIdx)}
                        onDragOver={e => onImgDragOver(e, imgIdx)}
                        onDrop={() => onImgDrop(sIdx)}
                        style={{ flex: '0 0 auto', width: isMobile ? 150 : 190,
                          scrollSnapAlign: 'start', borderRadius: 10, overflow: 'hidden',
                          border: '1.5px solid #e0e0e0', cursor: 'grab', userSelect: 'none', background: '#fff' }}
                      >
                        <div style={{ background: '#f0f0f0', padding: '4px 8px', display: 'flex',
                          alignItems: 'center', justifyContent: 'space-between' }}>
                          <span style={{ fontSize: 12, color: '#bbb', letterSpacing: 2 }}>⠿⠿</span>
                          <span style={{ fontSize: 10, color: '#aaa' }}>{imgIdx + 1}/{sec.images.length}</span>
                        </div>
                        <div style={{ position: 'relative' }}>
                          <img src={img.url} alt={`sec${sIdx}-img${imgIdx}`}
                            style={{ width: '100%', aspectRatio: '3/4', objectFit: 'cover', display: 'block' }} />
                          <button
                            onClick={e => { e.stopPropagation(); removeMenuImage(sIdx, imgIdx); }}
                            style={{ position: 'absolute', top: 5, right: 5, background: 'rgba(231,76,60,0.88)',
                              color: '#fff', border: 'none', borderRadius: 5, width: 24, height: 24,
                              cursor: 'pointer', fontSize: 11, fontWeight: 700, display: 'flex',
                              alignItems: 'center', justifyContent: 'center' }}>
                            ✕
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ── Add Category ── */}
      <div style={{ background: '#fff', borderRadius: 16, padding: isMobile ? 14 : 20, marginBottom: isMobile ? 16 : 24,
        boxShadow: '0 2px 12px rgba(0,0,0,0.08)' }}>
        <h3 style={{ marginBottom: 10, fontSize: isMobile ? 14 : 16, fontWeight: 700 }}>Yeni Kateqoriya</h3>
        <div style={{ display: 'flex', gap: 8, flexWrap: isMobile ? 'wrap' : 'nowrap' }}>
          <input
            value={newCatName}
            onChange={e => setNewCatName(e.target.value)}
            placeholder={isMobile ? 'Kateqoriya adı...' : 'Kateqoriya adı (məs. Qəhvə, Desert, Əsas Yeməklər)'}
            onKeyDown={e => e.key === 'Enter' && addCategory()}
            style={{ flex: 1, padding: '10px 12px', borderRadius: 8, border: '2px solid #e0e0e0',
              fontSize: 14, outline: 'none', minWidth: 0 }}
          />
          <select
            value={newCatStation}
            onChange={e => setNewCatStation(e.target.value)}
            style={{ padding: '10px 10px', borderRadius: 8, border: '2px solid #e0e0e0',
              fontSize: 13, background: '#fff', color: '#374151', flexShrink: 0 }}
          >
            {STATIONS.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
          <button onClick={addCategory}
            style={{ padding: '10px 16px', background: '#667eea', color: '#fff', border: 'none',
              borderRadius: 8, cursor: 'pointer', fontWeight: 700, fontSize: 14, flexShrink: 0 }}>
            + {isMobile ? '' : 'Əlavə Et'}
          </button>
        </div>
      </div>

      {/* ── Categories & Items ── */}
      {categories.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 60, color: '#aaa' }}>
          <div style={{ fontSize: 48, marginBottom: 12 }}>🍽️</div>
          <p>Hələ kateqoriya yoxdur. Yuxarıdan əlavə edin.</p>
        </div>
      ) : (
        categories.map(cat => {
          const catItems = items.filter(i => i.category_id === cat.id);
          return (
            <div key={cat.id} style={{ background: '#fff', borderRadius: 16, marginBottom: 20,
              boxShadow: '0 2px 12px rgba(0,0,0,0.08)', overflow: 'hidden' }}>

              {/* Category Header */}
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                padding: isMobile ? '10px 12px' : '14px 20px', background: '#f8f9ff', borderBottom: '1px solid #eee', gap: 8 }}>
                {editingCat?.id === cat.id ? (
                  <div style={{ display: 'flex', gap: 6, flex: 1, flexWrap: 'wrap' }}>
                    <input value={editingCat.name}
                      onChange={e => setEditingCat({ ...editingCat, name: e.target.value })}
                      onKeyDown={e => e.key === 'Enter' && saveCategory(cat)}
                      style={{ flex: 1, minWidth: 100, padding: '6px 10px', borderRadius: 6, border: '2px solid #667eea', fontSize: 14 }}
                      autoFocus />
                    <select
                      value={editingCat.station || 'Mətbəx'}
                      onChange={e => setEditingCat({ ...editingCat, station: e.target.value })}
                      style={{ padding: '6px 8px', borderRadius: 6, border: '2px solid #667eea', fontSize: 12, background: '#fff' }}
                    >
                      {STATIONS.map(s => <option key={s} value={s}>{s}</option>)}
                    </select>
                    <button onClick={() => saveCategory(cat)}
                      style={{ padding: '6px 12px', background: '#27ae60', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', fontWeight: 600 }}>✓</button>
                    <button onClick={() => setEditingCat(null)}
                      style={{ padding: '6px 10px', background: '#e74c3c', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer' }}>✕</button>
                  </div>
                ) : (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 1, minWidth: 0 }}>
                    <span style={{ fontWeight: 700, fontSize: isMobile ? 14 : 16, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{cat.name}</span>
                    <span style={{ fontSize: 11, background: '#667eea20', color: '#667eea',
                      padding: '2px 7px', borderRadius: 20, flexShrink: 0 }}>{catItems.length}</span>
                    {cat.station && (
                      <span style={{ fontSize: 10, background: '#fff7ed', color: '#f97316',
                        padding: '2px 7px', borderRadius: 20, fontWeight: 700, flexShrink: 0 }}>
                        🍳 {cat.station}
                      </span>
                    )}
                  </div>
                )}
                <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                  <button onClick={() => reorderCategory(cat, 'up')}
                    disabled={categories.indexOf(cat) === 0}
                    style={{ padding: '7px 9px', background: '#f1f5f9', color: '#475569', border: '1px solid #e2e8f0',
                      borderRadius: 6, cursor: categories.indexOf(cat) === 0 ? 'default' : 'pointer', fontSize: 13,
                      opacity: categories.indexOf(cat) === 0 ? 0.35 : 1 }}>▲</button>
                  <button onClick={() => reorderCategory(cat, 'down')}
                    disabled={categories.indexOf(cat) === categories.length - 1}
                    style={{ padding: '7px 9px', background: '#f1f5f9', color: '#475569', border: '1px solid #e2e8f0',
                      borderRadius: 6, cursor: categories.indexOf(cat) === categories.length - 1 ? 'default' : 'pointer', fontSize: 13,
                      opacity: categories.indexOf(cat) === categories.length - 1 ? 0.35 : 1 }}>▼</button>
                  <button onClick={() => openAddItem(cat.id)}
                    style={{ padding: isMobile ? '7px 10px' : '6px 14px', background: '#667eea', color: '#fff', border: 'none',
                      borderRadius: 6, cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>
                    {isMobile ? '+' : '+ Məhsul'}
                  </button>
                  <button onClick={() => setEditingCat({ id: cat.id, name: cat.name, station: cat.station || 'Mətbəx' })}
                    style={{ padding: '7px 9px', background: '#f39c12', color: '#fff', border: 'none',
                      borderRadius: 6, cursor: 'pointer', fontSize: 13 }}>✏️</button>
                  <button onClick={() => deleteCategory(cat.id)}
                    style={{ padding: '7px 9px', background: '#e74c3c', color: '#fff', border: 'none',
                      borderRadius: 6, cursor: 'pointer', fontSize: 13 }}>🗑️</button>
                </div>
              </div>

              {/* Items */}
              {catItems.length === 0 ? (
                <div style={{ padding: '20px', color: '#bbb', fontSize: 14, textAlign: 'center' }}>
                  Bu kateqoriyada məhsul yoxdur. "+ Məhsul" düyməsindən əlavə edin.
                </div>
              ) : (
                catItems.map(item => (
                  <div key={item.id} style={{
                    padding: isMobile ? '10px 12px' : '12px 20px',
                    borderBottom: '1px solid #f5f5f5',
                    opacity: item.is_available ? 1 : 0.55,
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      {/* Image */}
                      {item.image_url ? (
                        <img src={thumbUrl(item.image_url)} alt={item.name} loading="lazy"
                          style={{ width: isMobile ? 44 : 52, height: isMobile ? 44 : 52, borderRadius: 10, objectFit: 'cover',
                            border: '2px solid #eee', flexShrink: 0 }} />
                      ) : (
                        <div style={{ width: isMobile ? 44 : 52, height: isMobile ? 44 : 52, borderRadius: 10, background: '#f5f5f5',
                          display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, fontSize: 20 }}>🍽️</div>
                      )}

                      {/* Info */}
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                          {item.name && <span style={{ fontWeight: 600, fontSize: isMobile ? 13 : 14 }}>{item.name}</span>}
                          {!item.is_available && (
                            <span style={{ fontSize: 10, background: '#e74c3c20', color: '#e74c3c', padding: '1px 5px', borderRadius: 8 }}>Tükənib</span>
                          )}
                          {isChain && (
                            <span style={{
                              fontSize: 10, fontWeight: 700, padding: '1px 7px', borderRadius: 8,
                              background: item.location_id ? '#fff7ed' : '#f0fdf4',
                              color: item.location_id ? '#f97316' : '#10b981',
                            }}>
                              {item.location_id
                                ? `📍 ${locations.find(l => l.id === item.location_id)?.name || 'Filial'}`
                                : '🌐 Global'}
                            </span>
                          )}
                        </div>
                        {(item.description || item.description_en) && (
                          <div style={{ fontSize: 11, color: '#888', marginTop: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {item.description || item.description_en}
                          </div>
                        )}
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 4, flexWrap: 'wrap' }}>
                          <span style={{ fontWeight: 700, color: '#667eea', fontSize: isMobile ? 13 : 15 }}>
                            {item.variants?.length > 0
                              ? `${Math.min(...item.variants.map(v => v.price)).toFixed(2)}–${Math.max(...item.variants.map(v => v.price)).toFixed(2)} ₼`
                              : `${parseFloat(item.price).toFixed(2)} ₼`}
                          </span>
                          {item.avg_rating && (
                            <span style={{ fontSize: 11, color: '#92400E', background: '#FEF3C7', padding: '1px 7px', borderRadius: 8, fontWeight: 700 }}>
                              ★ {item.avg_rating} ({item.review_count})
                            </span>
                          )}
                        </div>
                      </div>

                      {/* Actions */}
                      <div style={{ display: 'flex', alignItems: 'center', gap: isMobile ? 5 : 8, flexShrink: 0 }}>
                        <button onClick={() => toggleAvailability(item)}
                          style={{
                            padding: isMobile ? '5px 8px' : '5px 12px', borderRadius: 8, cursor: 'pointer',
                            fontSize: isMobile ? 11 : 12, fontWeight: 700,
                            background: item.is_available ? '#f0fdf4' : '#fef2f2',
                            color: item.is_available ? '#16a34a' : '#dc2626',
                            border: item.is_available ? '1.5px solid #bbf7d0' : '1.5px solid #fecaca',
                          }}>
                          {isMobile ? (item.is_available ? '✓' : '✕') : (item.is_available ? '✓ Mövcuddur' : '✕ Tükənib')}
                        </button>
                        <button onClick={() => openEditItem(item)}
                          style={{ padding: isMobile ? '5px 8px' : '4px 10px', background: '#f39c12', color: '#fff', border: 'none',
                            borderRadius: 6, cursor: 'pointer', fontSize: 13 }}>✏️</button>
                        <button onClick={() => deleteItem(item.id)}
                          style={{ padding: isMobile ? '5px 8px' : '4px 10px', background: '#e74c3c', color: '#fff', border: 'none',
                            borderRadius: 6, cursor: 'pointer', fontSize: 13 }}>🗑️</button>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          );
        })
      )}

      {/* ── Item Modal ── */}
      {itemModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex',
          alignItems: isMobile ? 'flex-end' : 'center', justifyContent: 'center', zIndex: 1000 }}
          onClick={e => { if (e.target === e.currentTarget) setItemModal(null); }}>
          <div style={{
            background: '#fff',
            borderRadius: isMobile ? '16px 16px 0 0' : 16,
            padding: isMobile ? '20px 16px 36px' : 28,
            width: '100%', maxWidth: isMobile ? '100%' : 480,
            boxShadow: '0 -4px 40px rgba(0,0,0,0.2)',
            maxHeight: isMobile ? '92vh' : '90vh',
            overflowY: 'auto',
          }}>
            {isMobile && (
              <div style={{ textAlign: 'center', marginBottom: 14 }}>
                <div style={{ width: 40, height: 4, borderRadius: 2, background: '#CBD5E1', display: 'inline-block' }} />
              </div>
            )}
            <h3 style={{ marginBottom: 16, fontSize: isMobile ? 16 : 18, fontWeight: 700 }}>
              {itemModal.item ? '✏️ Məhsulu Düzəlt' : '+ Yeni Məhsul'}
            </h3>

            {/* Scope seçici — yalnız chain owner */}
            {isChain && (
              <div style={{ marginBottom: 16, padding: 14, background: '#f8faff', borderRadius: 12, border: '1.5px solid #e0e7ff' }}>
                <label style={{ fontSize: 13, fontWeight: 700, display: 'block', marginBottom: 10, color: '#374151' }}>
                  Hara əlavə et?
                </label>
                <div style={{ display: 'flex', gap: 8, marginBottom: itemScopeType === 'local' ? 10 : 0 }}>
                  <button
                    type="button"
                    onClick={() => setItemScopeType('global')}
                    style={{
                      flex: 1, padding: '9px 12px', borderRadius: 8, cursor: 'pointer',
                      fontWeight: 700, fontSize: 13, border: '2px solid',
                      borderColor: itemScopeType === 'global' ? '#10b981' : '#e5e7eb',
                      background: itemScopeType === 'global' ? '#f0fdf4' : '#fafafa',
                      color: itemScopeType === 'global' ? '#10b981' : '#666',
                    }}
                  >
                    🌐 Bütün Filiallara
                  </button>
                  <button
                    type="button"
                    onClick={() => setItemScopeType('local')}
                    style={{
                      flex: 1, padding: '9px 12px', borderRadius: 8, cursor: 'pointer',
                      fontWeight: 700, fontSize: 13, border: '2px solid',
                      borderColor: itemScopeType === 'local' ? '#f97316' : '#e5e7eb',
                      background: itemScopeType === 'local' ? '#fff7ed' : '#fafafa',
                      color: itemScopeType === 'local' ? '#f97316' : '#666',
                    }}
                  >
                    📍 Xüsusi Filial
                  </button>
                </div>
                {itemScopeType === 'local' && (
                  <select
                    value={itemTargetLocId || ''}
                    onChange={e => setItemTargetLocId(e.target.value)}
                    style={{ width: '100%', padding: '9px 12px', borderRadius: 8, border: '1.5px solid #f97316', fontSize: 13, background: '#fff', boxSizing: 'border-box' }}
                  >
                    {locations.map(loc => (
                      <option key={loc.id} value={loc.id}>
                        {loc.is_main ? '⭐ ' : '📍 '}{loc.name}
                      </option>
                    ))}
                  </select>
                )}
              </div>
            )}

            {/* Image upload */}
            <div style={{ marginBottom: 16 }}>
              <label style={{ fontSize: 13, fontWeight: 600, display: 'block', marginBottom: 8 }}>Şəkil</label>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                {itemForm.image_url ? (
                  <div style={{ position: 'relative' }}>
                    <img src={thumbUrl(itemForm.image_url)} alt="preview"
                      style={{ width: 72, height: 72, borderRadius: 12, objectFit: 'cover', border: '2px solid #eee' }} />
                    <button onClick={() => setItemForm(prev => ({ ...prev, image_url: '' }))}
                      style={{ position: 'absolute', top: -6, right: -6, background: '#e74c3c', color: '#fff',
                        border: 'none', borderRadius: '50%', width: 20, height: 20, cursor: 'pointer', fontSize: 11 }}>✕</button>
                  </div>
                ) : (
                  <div style={{ width: 72, height: 72, borderRadius: 12, background: '#f5f5f5',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 28 }}>🍽️</div>
                )}
                <div>
                  <button onClick={() => menuImageInputRef.current?.click()}
                    disabled={imageUploading}
                    style={{ padding: '8px 16px', background: '#667eea', color: '#fff', border: 'none',
                      borderRadius: 8, cursor: 'pointer', fontWeight: 600, fontSize: 13 }}>
                    {imageUploading ? 'Yüklənir...' : '📷 Şəkil Seç'}
                  </button>
                  <p style={{ fontSize: 11, color: '#aaa', marginTop: 4 }}>JPG, PNG — max 5MB</p>
                </div>
              </div>
              <input ref={menuImageInputRef} type="file" accept="image/*" style={{ display: 'none' }}
                onChange={handleMenuImageUpload} />
            </div>

            {/* Name / Description fields */}
            <div style={{ marginBottom: 14 }}>
              <div style={{ marginBottom: 10 }}>
                <label style={{ fontSize: 12, fontWeight: 600, display: 'block', marginBottom: 5, color: '#374151' }}>Ad *</label>
                <input value={itemForm.name} onChange={e => setItemForm({ ...itemForm, name: e.target.value })}
                  placeholder="Latte, Plov..."
                  style={{ width: '100%', padding: '9px 12px', borderRadius: 8, border: '1.5px solid #e0e0e0', fontSize: 14, boxSizing: 'border-box' }} />
              </div>
              <div>
                <label style={{ fontSize: 12, fontWeight: 600, display: 'block', marginBottom: 5, color: '#374151' }}>Təsvir</label>
                <input value={itemForm.description} onChange={e => setItemForm({ ...itemForm, description: e.target.value })}
                  placeholder="Qısa təsvir (ixtiyari)"
                  style={{ width: '100%', padding: '9px 12px', borderRadius: 8, border: '1.5px solid #e0e0e0', fontSize: 14, boxSizing: 'border-box' }} />
              </div>
            </div>

            {/* Variants / Price */}
            <div style={{ marginBottom: 14, padding: 14, background: '#f8faff', borderRadius: 10, border: '1.5px solid #e0e7ff' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
                <label style={{ fontSize: 13, fontWeight: 700, color: '#374151' }}>
                  {itemForm.variants.length > 0 ? '📐 Ölçü variantları' : '💰 Qiymət (₼)'}
                </label>
                <div style={{ display: 'flex', gap: 6 }}>
                  {itemForm.variants.length === 0 && (
                    <button type="button" onClick={applyPreset}
                      style={{ fontSize: 11, padding: '4px 10px', borderRadius: 6, border: '1.5px solid #667eea',
                        background: '#fff', color: '#667eea', cursor: 'pointer', fontWeight: 600 }}>
                      + Kiçik/Orta/Böyük
                    </button>
                  )}
                  <button type="button" onClick={addVariant}
                    style={{ fontSize: 11, padding: '4px 10px', borderRadius: 6, border: '1.5px solid #667eea',
                      background: '#fff', color: '#667eea', cursor: 'pointer', fontWeight: 600 }}>
                    + Variant əlavə et
                  </button>
                </div>
              </div>

              {itemForm.variants.length === 0 ? (
                <input type="number" step="0.01" min="0" value={itemForm.price}
                  onChange={e => setItemForm({ ...itemForm, price: e.target.value })}
                  placeholder="5.50"
                  style={{ width: '100%', padding: '10px 14px', borderRadius: 8,
                    border: '1.5px solid #e0e0e0', fontSize: 14, boxSizing: 'border-box' }} />
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {itemForm.variants.map((v, i) => (
                    <div key={i} style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                      <input
                        value={v.label}
                        onChange={e => updateVariant(i, 'label', e.target.value)}
                        placeholder="Ölçü adı (məs. Kiçik)"
                        style={{ flex: 1, padding: '8px 12px', borderRadius: 8, border: '1.5px solid #e0e0e0', fontSize: 13, boxSizing: 'border-box' }}
                      />
                      <input
                        type="number" step="0.01" min="0"
                        value={v.price}
                        onChange={e => updateVariant(i, 'price', e.target.value)}
                        placeholder="₼"
                        style={{ width: 80, padding: '8px 10px', borderRadius: 8, border: '1.5px solid #e0e0e0', fontSize: 13, boxSizing: 'border-box' }}
                      />
                      <button type="button" onClick={() => removeVariant(i)}
                        style={{ padding: '6px 10px', background: '#fee2e2', color: '#ef4444', border: 'none',
                          borderRadius: 8, cursor: 'pointer', fontWeight: 700, fontSize: 14 }}>✕</button>
                    </div>
                  ))}
                  <button type="button" onClick={() => setItemForm(prev => ({ ...prev, variants: [] }))}
                    style={{ fontSize: 12, color: '#94a3b8', background: 'none', border: 'none', cursor: 'pointer', textAlign: 'left', padding: 0 }}>
                    ✕ Variantları sil, tək qiymətə qayıt
                  </button>
                </div>
              )}
            </div>

            <div style={{ marginBottom: 20, display: 'flex', alignItems: 'center', gap: 10 }}>
              <input type="checkbox" id="available" checked={itemForm.is_available}
                onChange={e => setItemForm({ ...itemForm, is_available: e.target.checked })}
                style={{ width: 16, height: 16 }} />
              <label htmlFor="available" style={{ fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>
                Aktiv (sifarişə açıq)
              </label>
            </div>

            <div style={{ display: 'flex', gap: 10 }}>
              <button onClick={saveItem}
                style={{ flex: 1, padding: '12px', background: '#667eea', color: '#fff', border: 'none',
                  borderRadius: 8, cursor: 'pointer', fontWeight: 700, fontSize: 15 }}>
                {itemModal.item ? '💾 Yadda Saxla' : '✅ Əlavə Et'}
              </button>
              <button onClick={() => setItemModal(null)}
                style={{ padding: '12px 20px', background: '#f0f0f0', color: '#555', border: 'none',
                  borderRadius: 8, cursor: 'pointer', fontWeight: 600 }}>Ləğv</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MenuPage;
